import { useMemo } from 'react'

import type { TerrainMask } from '@hooks'
import {
  coordHash,
  groupByVariant,
  spawnDecorations,
  type DecorationConfig,
  type GeoInstance,
} from '@utils'

/**
 * Runs spawnDecorations + groupByVariant and returns one GeoInstance[] per variant.
 *
 * `config` must be stable across renders (module-level constant or memoized by caller)
 * since it is not included in deps — it contains functions that can't be compared by reference.
 * `scaleMin` and `scaleRange` are the only per-instance parameters that vary between families.
 */
export function useDecorationSpawner(
  mask: TerrainMask,
  config: DecorationConfig,
  variantCount: number,
  salt: number,
  scaleMin: number,
  scaleRange: number,
): GeoInstance[][] {
  return useMemo(() => {
    const spawned = spawnDecorations(mask, config, salt)
    return groupByVariant(spawned, variantCount, salt, (wx, wz) => ({
      scale: scaleMin + coordHash(wx, wz, salt + 20) * scaleRange,
      rotation: coordHash(wx, wz, salt + 30) * Math.PI * 2,
    }))
    // config contains functions — excluded from deps, must be stable (module-level or memoized)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mask, variantCount, salt, scaleMin, scaleRange])
}
